import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiArrowUpRight, FiMenu, FiX } from "react-icons/fi";
import NavLinks from "./NavLinks";
import FreeQuote from "../FreeQuotes/FreeQuote";

const navigation = [
  { label: "Home", to: "/" },
  { label: "About", to: "/aboutus" },
  { label: "FAQs", to: "/faq" },
  { label: "Contact Us", to: "/contactus" },
];

const Navbar = ({ compact = false }) => {
  const { pathname } = useLocation();
  const [showPopup, setShowPopup] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 18);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  const isActive = (to) =>
    to === "/" ? pathname === "/" : pathname.startsWith(to);

  const toggleQuote = () => setShowPopup((isOpen) => !isOpen);
  const openQuote = () => {
    setMenuOpen(false);
    setShowPopup(true);
  };

  const useDarkLogo = scrolled && !menuOpen;

  return (
    <>
      <header
        className={`site-header ${scrolled ? "is-scrolled" : ""} ${
          compact ? "is-compact" : ""
        } ${menuOpen ? "is-menu-open" : ""}`}
      >
        <div className="site-header__shell">
          <Link className="site-header__brand" to="/" aria-label="Abscod home">
            <img
              src={
                useDarkLogo
                  ? "/Images/abscodblack.webp"
                  : "/Images/abscodwhite.webp"
              }
              alt="Abscod Informatics"
              width="160"
              height="68"
            />
          </Link>

          <nav className="site-header__desktop-nav" aria-label="Main navigation">
            <ul className="site-nav__list">
              <li>
                <Link
                  className={`site-nav__link ${isActive("/") ? "is-active" : ""}`}
                  to="/"
                  aria-current={isActive("/") ? "page" : undefined}
                >
                  Home
                </Link>
              </li>
              <NavLinks onNavigate={() => setMenuOpen(false)} />
              {navigation.slice(1).map((item) => (
                <li key={item.to}>
                  <Link
                    className={`site-nav__link ${
                      isActive(item.to) ? "is-active" : ""
                    }`}
                    to={item.to}
                    aria-current={isActive(item.to) ? "page" : undefined}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="site-header__actions">
            <button className="site-header__cta" type="button" onClick={openQuote}>
              Request a quote
              <FiArrowUpRight aria-hidden="true" />
            </button>
            <button
              className="site-header__menu-button"
              type="button"
              onClick={() => setMenuOpen((isOpen) => !isOpen)}
              aria-expanded={menuOpen}
              aria-controls="mobile-navigation"
              aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            >
              {menuOpen ? <FiX aria-hidden="true" /> : <FiMenu aria-hidden="true" />}
            </button>
          </div>
        </div>

        <div
          id="mobile-navigation"
          className={`site-header__mobile ${menuOpen ? "is-open" : ""}`}
        >
          <div className="site-header__mobile-inner">
            <p className="site-header__mobile-label">Navigate</p>
            <ul className="site-header__mobile-list">
              <li>
                <Link
                  className={isActive("/") ? "is-active" : ""}
                  to="/"
                  onClick={() => setMenuOpen(false)}
                >
                  <span>Home</span><small>01</small>
                </Link>
              </li>
              <NavLinks onNavigate={() => setMenuOpen(false)} />
              {navigation.slice(1).map((item, index) => (
                <li key={item.to}>
                  <Link
                    className={isActive(item.to) ? "is-active" : ""}
                    to={item.to}
                    onClick={() => setMenuOpen(false)}
                  >
                    <span>{item.label}</span><small>0{index + 3}</small>
                  </Link>
                </li>
              ))}
            </ul>

            <div className="site-header__mobile-footer">
              <p>Have a project in mind?</p>
              <button type="button" onClick={openQuote}>
                Start a conversation <FiArrowUpRight aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {showPopup ? <FreeQuote popHandler={toggleQuote} /> : null}
    </>
  );
};

export default Navbar;
